import CloseIcon from '@mui/icons-material/Close'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import { IconButton } from '@mui/material'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { sendSupportReportToPatient } from 'src/supportPortal/services/supportBookedEventActionsService'

// Support sends the session's clinical document (prescription / therapy / screening / assessment report)
// to the patient FROM AUMHUM. The document is the practitioner's, but delivery is not on their behalf:
// AUMHUM delivers it over the generic patient-document channel, attributed to support. This dialog is
// itself the guardrail — it names who it goes to, who it's from, and requires an explicit confirm.
export default function SupportSendClinicalDocumentDialog({ open, onClose, contactContext, onSent }) {
  const [whatsapp, setWhatsapp] = useState(true)
  const [email, setEmail] = useState(true)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (open) {
      setWhatsapp(true)
      setEmail(true)
      setSending(false)
    }
  }, [open])

  const clinicalDocument = contactContext?.clinicalDocument || {}
  const reportType = clinicalDocument?.reportType
  const documentName = clinicalDocument?.documentName || 'document'
  const actionLabel = clinicalDocument?.label || `Send ${documentName}`
  const bookedEventId = contactContext?.bookedEventId
  const patientName = contactContext?.patientName || 'the patient'
  const providerName = contactContext?.clinicBrandName || ''
  const onBehalfOf = providerName || 'the clinic'

  async function handleSend() {
    const channels = [whatsapp ? 'whatsapp' : null, email ? 'email' : null].filter(Boolean)
    if (!channels.length) {
      toast.error('Pick at least one channel')
      return
    }
    if (!reportType || !bookedEventId) {
      toast.error('This booking is missing report details')
      return
    }
    setSending(true)
    const result = await sendSupportReportToPatient(reportType, bookedEventId, {
      channels,
      providerName: providerName || undefined,
    })
    setSending(false)
    if (result.ok) {
      toast.success(`${documentName} sent to ${patientName} via ${result.channels || 'the selected channels'}`)
      onSent?.(result)
      onClose?.()
      return
    }
    toast.error(result.message || `Could not send the ${documentName.toLowerCase()}.`)
  }

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
          color: '#fff',
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <DescriptionRoundedIcon sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {actionLabel}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5, wordBreak: 'break-word' }}>
              To {patientName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={sending} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={1.5}>
          <Alert
            severity="info"
            icon={<ShieldOutlinedIcon fontSize="inherit" />}
            sx={{ borderRadius: 2, '& .MuiAlert-message': { fontSize: 13 } }}
          >
            This <strong>{documentName}</strong> will be sent to {patientName} <strong>from AUMHUM</strong>{' '}
            on behalf of {onBehalfOf} — not from the practitioner. The document itself is the
            practitioner&apos;s saved {documentName.toLowerCase()}.
          </Alert>

          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            SEND VIA
          </Typography>
          <Box
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(148, 163, 184, 0.24)',
              px: 1.5,
              py: 0.5,
            }}
          >
            <FormControlLabel
              control={<Checkbox checked={whatsapp} onChange={(e) => setWhatsapp(e.target.checked)} />}
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <WhatsAppIcon sx={{ fontSize: 18, color: '#128c7e' }} />
                  <span>WhatsApp</span>
                </Stack>
              }
            />
            <FormControlLabel
              control={<Checkbox checked={email} onChange={(e) => setEmail(e.target.checked)} />}
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <EmailRoundedIcon sx={{ fontSize: 18, color: '#4f46e5' }} />
                  <span>Email</span>
                </Stack>
              }
            />
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(15, 23, 42, 0.55)' }}>
            Delivered as a secure document link; only channels on file for this patient are used.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          disabled={sending || (!whatsapp && !email)}
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            backgroundColor: '#4338ca',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#3730a3', boxShadow: 'none' },
          }}
        >
          {sending ? 'Sending…' : 'Send from AUMHUM'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
